import path from 'path'
import { useContext, useRef } from 'react'
import useUserContextRef from '../services/useUserContextRef'
import { AudioEngineRemoteAudio, RoomContext, RoomContextType } from '../RoomContext'
import * as Constants from '../services/Constants'
import { persistent } from '../services/LocalStorage'
import { AudioEngineError, IAudioEngineRoom, getAudioEngineInstance, AudioEngineRelayServer } from '../core/AudioEngine'
import useAudioSettingsStoreActions from '../core/useAudioSettingsStoreActions'
import { logger } from '../services/Logger'
import { AudioTrackWithInfo, UserContext, UserContextType } from '../UserContext'
import useAppContextRef from '../services/useAppContextRef'
import useRoomContextRef from '../services/useRoomContextRef'
import FakeMediaStreamTrack from '../core/AudioEngine/FakeMediaStreamTrack'
import { env } from '../services/EnvironmentVariables'
import { sleep } from '../utils'
import { MusePeer } from '../services/RoomContextMusePeer'
import moment from 'moment'

const log = logger.createChildLogger({ name: 'AudioEngine' })

export default function useAudioEngineActions() {
    const { dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const audioEngine = getAudioEngineInstance()

    const { setAudioInputSettings } = useAudioSettingsStoreActions()

    const { getAudioOutputSettings, getDefaultAudioOutputSettings, setAudioOutputSettings } =
        useAudioSettingsStoreActions()

    function joinAudioEngineRoom(roomUrn: string): Promise<[string | AudioEngineError, IAudioEngineRoom]> {
        return new Promise(async (resolve) => {
            const audioEngineRoom = audioEngine.createRoom()

            let relayNodes = Constants.data.relay_nodes
            const overrideNode = (persistent.getObject('audioEngineRelayOverride') as AudioEngineRelayServer) || null
            if (overrideNode) {
                relayNodes = [overrideNode]
            }

            const userInfo = {
                authToken: userStateRef.current.authToken,
                userUrn: userStateRef.current.userUrn,
                username: userStateRef.current.profile?.username || '',
            }

            // no await here -- roomDispatch needs to set audioEngineRoom object immediately
            // so that it can be used by other components.
            audioEngineRoom.joinRoom({ roomUrn, servers: relayNodes, userInfo }).then((r) => {
                resolve([r, audioEngineRoom])
            })

            roomDispatch({
                type: 'update_room_context',
                value: {
                    audioEngineRoom: audioEngineRoom,
                },
            })
        })
    }

    async function setAudioOutputDevice(audioOutputDeviceId: string): Promise<void> {
        return new Promise(async (resolve) => {
            function handleError() {
                log.error(`audioOutputSelectionResolver`, `could not find audio output device:`, {
                    mediaSourcesAudioOutputs: userStateRef.current.mediaSources.audioOutputs,
                    mediaSelectionAudioOutput: userStateRef.current.mediaSelection.audioOutput,
                })
            }

            if (audioOutputDeviceId === '_undef' || audioOutputDeviceId === '_none') {
                log.checkpoint(`set_audioengine_audio_output`, ``, { audioOutputDeviceId })
                audioEngine.setAudioOutputDevice(null, null)
                resolve()
                return
            }

            if (audioOutputDeviceId === 'default' || audioOutputDeviceId === '_default' || audioOutputDeviceId === '') {
                audioOutputDeviceId = 'default'
            }

            const source =
                userStateRef.current.mediaSources.audioOutputs.find((a) => a.deviceId === audioOutputDeviceId) || null
            if (source == null) {
                handleError()
                resolve()
                return
            }

            // query the audio engine for the definitive current sampleRate and bitDepth.
            let deviceSampleRate: number | null = null
            const devices = await getAudioEngineInstance().getAudioDevices()
            if (devices) {
                const match = devices.find((a) => a.deviceUid === source?.deviceUid)
                if (match) {
                    deviceSampleRate = match.defaultSampleRate
                }
            }

            let defaultOutputSettings = getDefaultAudioOutputSettings(source?.deviceId ?? audioOutputDeviceId)
            let settings = getAudioOutputSettings(source?.deviceId ?? audioOutputDeviceId)

            if (defaultOutputSettings == null || settings == null) {
                handleError()
                resolve()
                return
            }

            if (!settings._isDeviceSampleRateOverridden && deviceSampleRate) {
                settings.sampleRate = deviceSampleRate
            }

            if (env.TEST_AUDIO_ENGINE_DEVICE_OUTPUT_INVALID_SETTINGS) {
                // @ts-ignore
                settings.channelCount = null
            }

            log.checkpoint(`set_audioengine_audio_output`, ``, { source, settings, audioOutputDeviceId })
            audioEngine
                .setAudioOutputDevice(source, settings)
                .then((isOk) => {
                    log.verbose('audioOutputSelectionResolver', `audioEngine.setAudioOutputDevice() returned ${isOk}.`)
                    resolve()
                })
                .catch(() => {
                    // try one more time with default settings
                    log.checkpoint(`set_audioengine_audio_output`, ``, { source, settings: defaultOutputSettings })
                    audioEngine
                        .setAudioOutputDevice(source, defaultOutputSettings)
                        .then((isOk) => {
                            log.verbose(
                                'audioOutputSelectionResolver',
                                `audioEngine.setAudioOutputDevice() returned ${isOk}.`
                            )
                            if (defaultOutputSettings) {
                                setAudioOutputSettings(source.deviceId, defaultOutputSettings)
                            }
                            resolve()
                        })
                        .catch((e) => {
                            log.error(
                                `audioOutputSelectionResolver`,
                                `failed to set audioengine audio output device:`,
                                {
                                    source,
                                    settings,
                                    e,
                                }
                            )
                            dispatch({
                                type: 'update_media_streams',
                                value: {
                                    audioEngineAudioOutputError: e,
                                },
                            })
                            resolve()
                        })
                })
        })
    }

    const currentResyncWithAppStateInstIdRef = useRef(0)
    async function resyncWithAppState() {
        try {
            currentResyncWithAppStateInstIdRef.current += 1
            const thisResyncWithAppStateInstId = currentResyncWithAppStateInstIdRef.current

            log.info('resyncWithAppState', 'started.', { thisResyncWithAppStateInstId })

            const mediaSelection = userStateRef.current.mediaSelection
            let inputStreamsToRecover = userStateRef.current.mediaStreams.audioInputs.filter(
                (a) => a.info.isAudioEngine
            )

            await audioEngine.setSettings(appStateRef.current.appSettings.audioEngineDevSettings)

            // recover local audio output
            await setAudioOutputDevice(mediaSelection.audioOutput)

            // recover local audio inputs
            let recoveredLocalTracks: AudioTrackWithInfo[] = []
            for (let selection of mediaSelection.audioInputs) {
                if (inputStreamsToRecover.find((a) => a.info.deviceId === selection.deviceId) != null) {
                    const r = await audioEngine.getAudioStream(selection.source, selection.settings)
                    recoveredLocalTracks.push(r.track)
                    if (Object.keys(r.modifiedSettings).length > 0) {
                        const next = { ...selection.settings, ...r.modifiedSettings }
                        setAudioInputSettings(selection.deviceId, next)
                    }

                    // filter out the matching device in `audioInputsNext`.
                    inputStreamsToRecover = inputStreamsToRecover.filter((a) => a.info.label !== selection.source.label)
                    inputStreamsToRecover.push(r.track)
                }
            }
            log.isVerbose() &&
                log.verbose('resyncWithAppState', 'got recoveredLocalTracks: ', {
                    recoveredLocalTracks: recoveredLocalTracks.map((a) => a.info),
                })

            const recoveredRemoteAudioStreams: Record<string, AudioEngineRemoteAudio[]> = {}

            // join room
            let audioEngineRoom = roomStateRef.current.audioEngineRoom
            if (audioEngineRoom && roomStateRef.current.roomUrn) {
                let [r, audioEngineRoom] = await joinAudioEngineRoom(roomStateRef.current.roomUrn)
                log.isVerbose() && log.verbose('resyncWithAppState', 'joinAudioEngineRoom returned.', { r })

                // recover receiving remote audio streams
                for (let sessionId of Object.keys(roomStateRef.current.peers)) {
                    const peer = roomStateRef.current.peers[sessionId]
                    for (let s of peer.remoteAudios) {
                        if (s.fromTopology === 'ae' && s.track instanceof FakeMediaStreamTrack) {
                            let r = await audioEngineRoom.receiveRemoteStream(s.track.id, s.info)
                            log.info('resyncWithAppState', 'called receiveRemoteStream()', {
                                streamUuid: s.track.id,
                                info: s.info,
                                r: r instanceof AudioEngineError ? r : '<AudioEngineRemoteAudio>',
                            })

                            if ('fromTopology' in r) {
                                if (!recoveredRemoteAudioStreams[sessionId]) {
                                    recoveredRemoteAudioStreams[sessionId] = []
                                }
                                recoveredRemoteAudioStreams[sessionId].push(r)
                            }
                        }
                    }
                }

                // NOTE: recover sending audio streams to remote is done when
                // `update_media_streams` is dispatched.
            }

            if (roomStateRef.current.isScreenRecording) {
                const ts = moment().format('HHmmss')
                const info = roomStateRef.current.isScreenRecording
                const newFileName = path.join(
                    info.outputFileDirectory,
                    info.outputFilenameBase + `-pro-crash-recovery-${ts}.mp3`
                )
                audioEngine.startMasterCaptureToFile(newFileName)
            }

            if (env.TEST_OS_AUDIO_SYSTEM_RESET) {
                // for testing race condition; see #1145
                await sleep(3000)
            }

            const inputStreamsNonAe = userStateRef.current.mediaStreams.audioInputs.filter((a) => !a.info.isAudioEngine)
            // append `aiStreamsToRecover` to `aiStreamsNext`, but if there's one already
            // present (by id), then skip.
            const inputStreamsNext = [...inputStreamsNonAe]
            for (const stream of inputStreamsToRecover) {
                if (!inputStreamsNext.some((s) => s.id === stream.id)) {
                    inputStreamsNext.push(stream)
                }
            }

            log.info('resyncWithAppState', 'completed.', {
                inputStreamsToRecover: inputStreamsToRecover.map((a) => a.id),
                inputStreamsNonAe: inputStreamsNonAe.map((a) => a.id),
                inputStreamsNext: inputStreamsNext.map((a) => a.id),
            })

            dispatch({
                type: 'update_media_streams',
                value: {
                    audioInputs: inputStreamsNext,
                },
            })

            const updatePeersData: { sessionId: string; peer: Partial<MusePeer> }[] = []
            Object.entries(roomStateRef.current.peers).forEach(([sessionId, peer]) => {
                updatePeersData.push({
                    sessionId,
                    peer: {
                        remoteAudios: [
                            ...peer.remoteAudios.filter((a) => a.fromTopology !== 'ae'),
                            ...(recoveredRemoteAudioStreams[sessionId] || []),
                        ],
                    },
                })
            })

            log.isVerbose() &&
                log.verbose('resyncWithAppState', 'updating RoomContext.peers.', {
                    updatePeersData: stringifyUpdatePeersData(updatePeersData),
                })

            if (thisResyncWithAppStateInstId === currentResyncWithAppStateInstIdRef.current) {
                roomDispatch({
                    type: 'update_peer',
                    value: updatePeersData,
                })
            } else {
                log.isVerbose() &&
                    log.verbose('resyncWithAppState', 'skipping roomDispatch because another instance has started.', {
                        thisResyncWithAppStateInstId,
                        currentResyncWithAppStateInstId: currentResyncWithAppStateInstIdRef.current,
                    })
            }
        } catch (e) {
            log.assert('resyncWithAppState', 'an unexpected exception occurred at resyncWithUpdate(): ', { e })
        }
    }

    return {
        joinAudioEngineRoom,
        setAudioOutputDevice,
        resyncWithAppState,
    }
}

function stringifyUpdatePeersData(updatePeersData: { sessionId: string; peer: Partial<MusePeer> }[]) {
    return updatePeersData.map((a) => {
        return {
            sessionId: a.sessionId,
            peer: {
                remoteAudios: (a.peer.remoteAudios || []).map((b) => ({
                    fromTopology: b.fromTopology,
                    trackId: b.track?.id,
                    info: {
                        deviceId: b.info?.deviceId,
                        label: b.info?.displayLabel,
                        isAudioEngine: b.info?.isAudioEngine,
                        audioEngineDeviceId: b.info?.audioEngineDeviceId,
                        audioEngineStreamUuid: b.info?.audioEngineStreamUuid,
                    },
                })),
            },
        }
    })
}
